import { useState, useMemo } from "react";
import { Card, Table, Tag, message, Select, Empty, Spin, Button, Modal, Form, Input, ColorPicker, Drawer, Checkbox, Popconfirm, Space } from "antd";
import { useCompanyList } from "../../../api-hooks/companies";
import { useRolesByCompany } from "../../../api-hooks/admins";
import { useAllPermissions, useCreateRole, useUpdateRolePermissions, useDeleteRole } from "../../../api-hooks/roles";
import type { TRole } from "../../../types";

export default function Roles() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [messageApi, ctx] = message.useMessage();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<TRole | null>(null);
  const [checkedPerms, setCheckedPerms] = useState<number[]>([]);
  const [form] = Form.useForm();

  const { data: companies = [], isLoading: companiesLoading } = useCompanyList({ limit: 200 });
  const { data: roles = [], isLoading: rolesLoading } = useRolesByCompany(selectedCompanyId);
  const { data: allPerms = [] } = useAllPermissions();

  const createRole = useCreateRole({
    onSuccess: () => { messageApi.success("Role created"); setCreateOpen(false); form.resetFields(); },
    onError: () => messageApi.error("Failed to create role"),
  });
  const updatePerms = useUpdateRolePermissions({
    onSuccess: () => { messageApi.success("Permissions saved"); setEditingRole(null); },
    onError: () => messageApi.error("Failed to save permissions"),
  });
  const deleteRole = useDeleteRole({
    onSuccess: () => messageApi.success("Role deleted"),
    onError: () => messageApi.error("Cannot delete system role"),
  });

  const permsByModule = useMemo(() => {
    const map: Record<string, any[]> = {};
    allPerms.forEach((p) => {
      if (!map[p.module]) map[p.module] = [];
      map[p.module].push(p);
    });
    return map;
  }, [allPerms]);

  const openPermEditor = (role: TRole) => {
    setEditingRole(role);
    const granted = (role.permissions ?? []).filter((p) => p.granted).map((p) => p.id);
    setCheckedPerms(granted);
  };

  const toggleModule = (modulePerms: any[], checked: boolean) => {
    const ids = modulePerms.map((p) => p.id);
    setCheckedPerms((prev) =>
      checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))
    );
  };

  const roleColumns = [
    {
      title: "Role", dataIndex: "name", render: (v: string, row: TRole) => (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: row.colour || "#7c3aed" }} />
          <span className="font-medium">{v}</span>
          {row.is_system && <Tag className="text-[10px] px-1 py-0 m-0">system</Tag>}
        </div>
      ),
    },
    { title: "Slug", dataIndex: "slug", render: (v: string) => <span className="font-mono text-xs text-slate-500">{v}</span> },
    { title: "Description", dataIndex: "description", ellipsis: true },
    {
      title: "Permissions", render: (_: any, row: TRole) => {
        const count = (row.permissions ?? []).filter((p) => p.granted).length;
        return <Tag color="purple">{count} granted</Tag>;
      },
    },
    {
      title: "Actions", key: "actions", render: (_: any, row: TRole) => (
        <Space size="small">
          <Button size="small" type="primary" ghost onClick={() => openPermEditor(row)}>Edit Permissions</Button>
          {!row.is_system && (
            <Popconfirm title="Delete this role?" onConfirm={() => deleteRole.mutate(row.id)}>
              <Button size="small" danger type="text">Delete</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Roles & RBAC</h2>
          <p className="text-slate-500 text-sm mt-0.5">Manage roles and permissions per company</p>
        </div>
        {selectedCompanyId && (
          <Button type="primary" onClick={() => { form.resetFields(); setCreateOpen(true); }}>Create Role</Button>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600 font-medium">Company:</span>
          <Select
            placeholder="Choose a company..."
            loading={companiesLoading}
            value={selectedCompanyId ?? undefined}
            onChange={(v) => setSelectedCompanyId(v)}
            showSearch
            optionFilterProp="label"
            style={{ width: 300 }}
            options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
          />
        </div>
      </Card>

      {!selectedCompanyId ? (
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <Empty description="Select a company to manage its roles" />
        </Card>
      ) : rolesLoading ? (
        <div className="flex justify-center py-12"><Spin size="large" /></div>
      ) : (
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <Table
            dataSource={roles}
            columns={roleColumns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Create Role Modal */}
      <Modal title="Create Role" open={createOpen} onCancel={() => setCreateOpen(false)}
        onOk={() => form.validateFields().then((v) => {
          const colour = typeof v.colour === "object" ? `#${v.colour.toHex()}` : (v.colour || "#7c3aed");
          createRole.mutate({ companyId: selectedCompanyId!, data: { ...v, colour } });
        })}
        okText="Create" confirmLoading={createRole.isPending}>
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="slug" label="Slug" rules={[{ required: true, pattern: /^[a-z0-9_]+$/, message: "lowercase, numbers, underscores only" }]}>
            <Input placeholder="my_role" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="colour" label="Colour" initialValue="#7c3aed">
            <ColorPicker showText />
          </Form.Item>
        </Form>
      </Modal>

      {/* Permission Editor Drawer */}
      <Drawer
        title={`Edit Permissions — ${editingRole?.name || ""}`}
        open={!!editingRole}
        onClose={() => setEditingRole(null)}
        width={520}
        extra={
          <Button type="primary" loading={updatePerms.isPending}
            onClick={() => editingRole && updatePerms.mutate({ roleId: editingRole.id, permission_ids: checkedPerms })}>
            Save Permissions
          </Button>
        }
      >
        {Object.entries(permsByModule).map(([mod, perms]) => {
          const allChecked = perms.every((p) => checkedPerms.includes(p.id));
          const someChecked = perms.some((p) => checkedPerms.includes(p.id));
          return (
            <div key={mod} className="mb-4">
              <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-100">
                <Checkbox
                  indeterminate={someChecked && !allChecked}
                  checked={allChecked}
                  onChange={(e) => toggleModule(perms, e.target.checked)}
                />
                <span className="font-semibold text-slate-700 capitalize text-sm">{mod.replace(/_/g, " ")}</span>
              </div>
              <div className="flex flex-wrap gap-2 pl-6">
                {perms.map((p) => (
                  <Checkbox
                    key={p.id}
                    checked={checkedPerms.includes(p.id)}
                    onChange={(e) => setCheckedPerms((prev) =>
                      e.target.checked ? [...prev, p.id] : prev.filter((id) => id !== p.id)
                    )}
                  >
                    <span className="text-xs">{p.action}</span>
                  </Checkbox>
                ))}
              </div>
            </div>
          );
        })}
      </Drawer>
    </div>
  );
}
